import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { DropdownList, Stack, Text } from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';
import type { z } from 'zod';

import { useAllDepartments } from '~/api/query/departmentAll';
import { DropdownInput } from '~/components/DropdownInput';
import {
  abbottAdminSchema,
  basicUserSchema,
  schemaMap,
  userSchema,
  validRoles,
} from '~/components/forms/adminUser/validations';
import { CLMEmailInput } from '~/components/inputs/CLMEmailInput';
import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import { CLMTextInput } from '~/components/inputs/CLMTextInput';
import {
  ALLOWED_ROLE_CREATION,
  ALLOWED_ROLE_CREATION_BY_DOMAIN,
  USER_ROLE_NAME,
  USER_ROLES,
} from '~/features/auth/roles';
import { useAuthState } from '~/features/auth/useAuthState';
import { DOMAINS } from '~/features/navigation/domains';
import { useDomainName } from '~/features/navigation/useDomainName';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

type SchemaUnion = typeof userSchema | typeof abbottAdminSchema;
export type UserSubmitValues = z.infer<SchemaUnion>;
type BasicFormValues = z.input<typeof basicUserSchema>;

export type AdminUserFormRef = {
  submitForm: () => Promise<void>;
};

type AdminUserFormProps = {
  testID: string;
  onSubmit: (values: UserSubmitValues) => Promise<void>;
  defaultValues?: Partial<UserSubmitValues>;
  hiddenFields?: (keyof UserSubmitValues)[];
  hasFormChanged?: (isDirty: boolean) => void;
};

const baseDefaultValues: BasicFormValues = {
  email: '',
  phone: '',
  firstName: '',
  lastName: '',
  departmentId: null,
};

export const AdminUserForm = forwardRef<AdminUserFormRef, AdminUserFormProps>(
  (
    {
      testID,
      onSubmit,
      defaultValues: passedDefaults,
      hiddenFields,
      hasFormChanged,
    },
    ref,
  ) => {
    const domain = useDomainName();
    const { data: departments = [], isLoading: isDepartmentsLoading } =
      useAllDepartments();

    const validationSchema = schemaMap[domain];

    const form = useForm({
      defaultValues: {
        ...baseDefaultValues,
        ...passedDefaults,
      },
      validators: { onChange: validationSchema },
      onSubmit: ({ value }) => {
        const { departmentId, ...cleaned } = value;

        return onSubmit(
          validationSchema.parse(departmentId === null ? cleaned : value),
        );
      },
    });

    useEffect(() => {
      if (hasFormChanged) {
        hasFormChanged(form.state.isDirty);
      }
    }, [form.state.isDirty, hasFormChanged]);

    const { userInfo } = useAuthState();
    const userRole = userInfo?.role;

    const roleItems = useMemo(() => {
      if (!userRole) {
        return [];
      }
      const allowedRoleValues = ALLOWED_ROLE_CREATION[userRole].filter((role) =>
        ALLOWED_ROLE_CREATION_BY_DOMAIN[domain].includes(role),
      );

      return allowedRoleValues
        .filter((role) => validRoles.includes(role))
        .map((role) => ({
          value: role,
          label: USER_ROLE_NAME[role],
        }));
    }, [domain, userRole]);

    const { departmentId: mainDepartmentValue, role: roleValue } = useStore(
      form.store,
      (s) => s.values,
    );

    const hasSelectedRole = !!roleValue;
    const isHcpAdmin = roleValue === USER_ROLES.HcpAdmin;
    const isNonHcpAdminSelected = roleValue === USER_ROLES.NonHcpAdmin;

    // When NonHcpAdmin is selected, department fields are hidden; clear value to avoid stale submit.
    useEffect(() => {
      if (isNonHcpAdminSelected) {
        form.setFieldValue('departmentId', null);
      }
    }, [isNonHcpAdminSelected, form]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: form.handleSubmit,
      }),
      [form.handleSubmit],
    );

    /**
     * Department Access (left dropdown)
     * - Default (no role selected): shows "All Departments" (disabled)
     * - HcpAdmin: shows "All Departments" (disabled)
     * - Other roles: shows department list and selects departmentId
     */
    const accessDropdownItems = useMemo(() => {
      if (!hasSelectedRole) return ['All Departments'];

      return isHcpAdmin ? ['All Departments'] : departments.map((d) => d.name);
    }, [hasSelectedRole, isHcpAdmin, departments]);

    const accessDropdownIndex = useMemo(() => {
      if (!hasSelectedRole) {
        return 0;
      }
      if (isHcpAdmin) {
        return 0;
      }

      if (!mainDepartmentValue) {
        return undefined;
      }
      const depIndex = departments.findIndex(
        (d) => d.id === mainDepartmentValue,
      );

      return depIndex >= 0 ? depIndex : undefined;
    }, [hasSelectedRole, isHcpAdmin, mainDepartmentValue, departments]);

    const accessDropdownValue = useMemo(() => {
      if (!hasSelectedRole) {
        return 'All Departments';
      }
      if (isHcpAdmin) {
        return 'All Departments';
      }

      return accessDropdownIndex !== undefined
        ? departments[accessDropdownIndex]?.name
        : '';
    }, [hasSelectedRole, isHcpAdmin, accessDropdownIndex, departments]);

    const primaryDeptItems = useMemo(
      () => ['None', ...departments.map((d) => d.name)],
      [departments],
    );

    const primaryDeptIndex = useMemo(() => {
      if (!mainDepartmentValue) {
        return 0;
      }
      const foundIndex = departments.findIndex(
        (d) => d.id === mainDepartmentValue,
      );

      return foundIndex >= 0 ? foundIndex + 1 : 0;
    }, [mainDepartmentValue, departments]);

    const primaryDeptValue = useMemo(() => {
      if (primaryDeptIndex === 0) {
        return '';
      }

      return departments[primaryDeptIndex - 1]?.name ?? '';
    }, [primaryDeptIndex, departments]);

    const showDepartmentAccessFields =
      !hiddenFields?.includes('departmentId') &&
      domain !== DOMAINS.ABBOTT_ADMINISTRATION &&
      !isNonHcpAdminSelected;

    return (
      <Stack gap="$6" paddingTop="$2" testID={testID}>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            {!hiddenFields?.includes('firstName') && (
              <form.Field name="firstName">
                {(field) => (
                  <CLMTextInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'first', 'name'],
                    })}
                    label="First name"
                    placeholder="First name"
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            )}
          </Stack>

          <Stack flex={1}>
            {!hiddenFields?.includes('lastName') && (
              <form.Field name="lastName">
                {(field) => (
                  <CLMTextInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'last', 'name'],
                    })}
                    label="Last name"
                    placeholder="Last name"
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            )}
          </Stack>
        </Stack>

        <Text type="$bodyBase.medium" color="$text.100">
          Contact Information
        </Text>

        <Stack flexDirection="row" gap="$6">
          <Stack flex={1} flexBasis="50%">
            {!hiddenFields?.includes('email') && (
              <form.Field name="email">
                {(field) => (
                  <CLMEmailInput
                    testID={buildTestId({ testID, suffix: ['input', 'email'] })}
                    label="Email address"
                    placeholder="Email"
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            )}
          </Stack>

          <Stack flex={1} flexBasis="50%">
            {!hiddenFields?.includes('phone') && (
              <form.Field name="phone">
                {(field) => (
                  <CLMPhoneInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'phone'],
                    })}
                    value={field.state.value}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    {...getErrorProps(field)}
                  />
                )}
              </form.Field>
            )}
          </Stack>
        </Stack>

        <Text type="$bodyBase.medium" color="$text.100">
          Access
        </Text>

        {!hiddenFields?.includes('role') && (
          <form.Field name="role">
            {(field) => (
              <DropdownList
                testID={buildTestId({ testID, suffix: ['dropdown', 'role'] })}
                items={roleItems.map((i) => i.label)}
                selectedIndex={
                  field.state.value
                    ? roleItems.findIndex((i) => i.value === field.state.value)
                    : undefined
                }
                onSelect={(index) => {
                  const selected = roleItems[index]?.value;

                  if (selected) {
                    field.handleChange(selected);

                    form.setFieldValue(
                      'departmentId',
                      form.getFieldValue('departmentId'),
                    );
                  }
                }}
              >
                <DropdownInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'type', 'role'],
                  })}
                  aria-label="User Type"
                  label="User type"
                  placeholder="Select"
                  value={
                    field.state.value
                      ? (roleItems.find((i) => i.value === field.state.value)
                          ?.label ?? '')
                      : ''
                  }
                  {...getErrorProps(field)}
                />
              </DropdownList>
            )}
          </form.Field>
        )}

        {showDepartmentAccessFields && (
          <Stack flexDirection="row" gap="$6">
            <Stack flex={1}>
              <form.Field name="departmentId">
                {(field) => (
                  <DropdownList
                    testID={buildTestId({
                      testID,
                      suffix: ['dropdown', 'department', 'access'],
                    })}
                    items={accessDropdownItems}
                    selectedIndex={accessDropdownIndex}
                    onSelect={(index) => {
                      if (!hasSelectedRole || isHcpAdmin) {
                        return;
                      }

                      const dept = departments[index];

                      if (dept) field.handleChange(dept.id);
                    }}
                  >
                    <DropdownInput
                      testID={buildTestId({
                        testID,
                        suffix: ['input', 'type', 'department', 'access'],
                      })}
                      aria-label="Department Access"
                      label="Department access"
                      placeholder="Select"
                      disabled={!hasSelectedRole || isHcpAdmin}
                      value={accessDropdownValue}
                      {...getErrorProps(field)}
                    />
                  </DropdownList>
                )}
              </form.Field>
            </Stack>

            {isHcpAdmin && (
              <Stack flex={1}>
                <form.Field name="departmentId">
                  {(field) => (
                    <DropdownList
                      testID={buildTestId({
                        testID,
                        suffix: ['dropdown', 'department'],
                      })}
                      items={primaryDeptItems}
                      selectedIndex={primaryDeptIndex}
                      onSelect={(index) => {
                        if (index === 0) {
                          field.handleChange(null);
                        } else {
                          const selectedId = departments[index - 1]?.id;

                          field.handleChange(selectedId ?? null);
                        }
                        field.handleBlur();
                      }}
                    >
                      <DropdownInput
                        testID={buildTestId({
                          testID,
                          suffix: ['input', 'type', 'department'],
                        })}
                        aria-label="Primary Department (Optional)"
                        label="Primary department (Optional)"
                        placeholder={
                          isDepartmentsLoading ? 'Loading...' : 'Select'
                        }
                        value={primaryDeptValue}
                      />
                    </DropdownList>
                  )}
                </form.Field>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);

AdminUserForm.displayName = 'AdminUserForm';
